import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LifeBuoy, Paperclip, Send, ThumbsUp, ThumbsDown, HelpCircle, Archive, CheckCircle2, RotateCcw, Check, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError, API_BASE } from '../../api';
import PasteDropZone, { StagedFiles, imagesFromClipboard, withinSizeLimit } from '../../components/PasteDropZone';

const TYPE_LABEL = { issue: 'Issue', improvement: 'Improvement request', question: 'General question' };
const STATUS_TONE = {
    submitted: 'bg-sky-100 text-sky-800',
    awaiting_information: 'bg-amber-100 text-amber-800',
    awaiting_client_approval: 'bg-amber-100 text-amber-800',
    under_review: 'bg-violet-100 text-violet-800',
    approved: 'bg-violet-100 text-violet-800',
    backlog: 'bg-slate-200 text-slate-700',
    planned: 'bg-indigo-100 text-indigo-800',
    in_progress: 'bg-blue-100 text-blue-800',
    awaiting_client_validation: 'bg-amber-100 text-amber-800',
    completed: 'bg-emerald-100 text-emerald-800',
    closed: 'bg-slate-200 text-slate-600',
    declined: 'bg-rose-100 text-rose-800',
};
const CLIENT_STAGES = [
    { key: 'received', label: 'Received' },
    { key: 'reviewing', label: 'Reviewing' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'resolved', label: 'Resolved' },
];

function formatWhen(iso) {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return null; }
}

export default function PublicTicketView() {
    const { token } = useParams();
    const [ticket, setTicket] = useState(null);
    const [err, setErr] = useState(null);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [approverName, setApproverName] = useState('');
    const [approvalComment, setApprovalComment] = useState('');
    const [decidingAction, setDecidingAction] = useState(null);
    const [validateComment, setValidateComment] = useState('');
    const [validating, setValidating] = useState(false);
    const [replyFiles, setReplyFiles] = useState([]);
    const replyFileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/public/support/tickets/${token}`);
            setTicket(r.data);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || 'Request not found');
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);
    // Clients can paste a screenshot anywhere on their tracking page; it's
    // staged on the reply box below (zone pastes are already handled there).
    useEffect(() => {
        const h = (e) => {
            if (e.defaultPrevented) return;
            const imgs = withinSizeLimit(imagesFromClipboard(e));
            if (!imgs.length) return;
            e.preventDefault();
            setReplyFiles(prev => [...prev, ...imgs]);
            toast.message('Screenshot added to your reply');
        };
        window.addEventListener('paste', h);
        return () => window.removeEventListener('paste', h);
    }, []);

    const sendReply = async () => {
        if (!reply.trim() && !replyFiles.length) return;
        setSending(true);
        try {
            let attachment_ids = [];
            if (replyFiles.length) {
                const fd = new FormData();
                replyFiles.forEach(f => fd.append('files', f));
                fd.append('context', 'comment');
                const up = await api.post(`/public/support/tickets/${token}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                attachment_ids = up.data.items.map(a => a.id);
            }
            await api.post(`/public/support/tickets/${token}/comments`, { body: reply.trim() || 'See attached screenshot.', attachment_ids });
            setReply(''); setReplyFiles([]);
            load();
            toast.success('Reply sent');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to send'); }
        finally { setSending(false); }
    };

    const decide = async (decision) => {
        setDecidingAction(decision);
        try {
            await api.post(`/public/support/tickets/${token}/approval`, {
                decision, comment: approvalComment.trim() || null, decided_by_name: approverName.trim() || null,
            });
            setApprovalComment('');
            load();
            toast.success('Decision recorded — thank you');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to submit decision'); }
        finally { setDecidingAction(null); }
    };

    const validate = async (confirmed) => {
        setValidating(true);
        try {
            await api.post(`/public/support/tickets/${token}/validate`, { confirmed, comment: validateComment.trim() || null });
            setValidateComment('');
            load();
            toast.success(confirmed ? 'Thanks for confirming!' : 'Request reopened — we will take another look');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to submit'); }
        finally { setValidating(false); }
    };

    if (err) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600" data-testid="public-ticket-error">{err}</div>;
    if (!ticket) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

    const tone = STATUS_TONE[ticket.status] || 'bg-slate-100 text-slate-700';
    const accent = ticket.platform_accent_color || '#0F5F6B';
    const stageIdx = CLIENT_STAGES.findIndex(s => s.key === ticket.client_stage);

    return (
        <div className="min-h-screen bg-slate-50" data-testid="public-ticket-view">
            <header className="text-white px-6 py-4" style={{ background: accent }}>
                <div className="max-w-2xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <LifeBuoy className="w-5 h-5 text-white/80" />
                        <div className="text-sm font-semibold">{ticket.platform_name} Support</div>
                    </div>
                    <div className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${tone}`} data-testid="public-ticket-status">
                        {ticket.status_label}
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-6 py-8 space-y-5">
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="public-ticket-progress-tracker">
                    <div className="flex items-center">
                        {CLIENT_STAGES.map((s, i) => (
                            <React.Fragment key={s.key}>
                                <div className="flex flex-col items-center gap-1.5">
                                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                                         style={{ background: i <= stageIdx ? accent : '#e2e8f0', color: i <= stageIdx ? '#fff' : '#94a3b8' }}
                                         data-testid={`public-stage-dot-${s.key}`}>
                                        {i < stageIdx ? <Check size={13} /> : i + 1}
                                    </div>
                                    <span className={`text-[10.5px] font-medium ${i <= stageIdx ? 'text-slate-700' : 'text-slate-400'}`}>{s.label}</span>
                                </div>
                                {i < CLIENT_STAGES.length - 1 && (
                                    <div className="flex-1 h-[2px] mx-1 mb-4" style={{ background: i < stageIdx ? accent : '#e2e8f0' }} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    {ticket.expected_response_by && (
                        <div className="mt-4 text-center text-[12px] text-slate-500 bg-slate-50 rounded-md py-2" data-testid="public-ticket-expected-response">
                            We aim to give you an update by <strong className="text-slate-700">{formatWhen(ticket.expected_response_by)}</strong>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                        <span>{ticket.ticket_number}</span>
                        <span>·</span>
                        <span>{TYPE_LABEL[ticket.request_type]}</span>
                    </div>
                    <h1 className="text-[19px] font-bold text-slate-900 mt-1">{ticket.title}</h1>
                    <p className="text-[13.5px] text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
                    {ticket.extra_info && (
                        <div className="mt-3 text-[12.5px] text-slate-500 border-t border-slate-100 pt-3">
                            <span className="font-medium text-slate-600">Additional info: </span>{ticket.extra_info}
                        </div>
                    )}
                    {(ticket.attachments || []).filter(a => a.context !== 'comment').length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {ticket.attachments.filter(a => a.context !== 'comment').map(a => (
                                <a key={a.id} href={`${API_BASE}/public/support/attachments/${ticket.id}/${a.id}`} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center gap-1.5 text-[12px] bg-slate-100 rounded-full px-3 py-1.5 text-slate-700 hover:bg-slate-200"
                                   data-testid={`public-ticket-attachment-${a.id}`}>
                                    <Paperclip size={12} />{a.name}
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                {ticket.status === 'awaiting_client_approval' && ticket.request_type === 'improvement' && (
                    <div className="bg-white rounded-xl border border-amber-300 p-6 shadow-sm" data-testid="public-ticket-approval-panel">
                        <div className="font-semibold text-[14px] text-amber-800 mb-1">This improvement needs your approval</div>
                        <p className="text-[12.5px] text-slate-500 mb-3">Let us know how you'd like to proceed. You can add a comment with your decision.</p>
                        <input value={approverName} onChange={e => setApproverName(e.target.value)} placeholder="Your name (optional)"
                               className="w-full h-9 rounded-md border border-slate-300 px-3 text-[13px] mb-2" data-testid="public-approval-name-input" />
                        <textarea value={approvalComment} onChange={e => setApprovalComment(e.target.value)} rows={2} placeholder="Comment (optional)"
                                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] mb-3" data-testid="public-approval-comment-input" />
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => decide('approve')} disabled={!!decidingAction}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 text-white text-[12.5px] font-semibold disabled:opacity-50" data-testid="public-approval-approve-btn">
                                <ThumbsUp size={13} /> Approve
                            </button>
                            <button onClick={() => decide('backlog')} disabled={!!decidingAction}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-600 text-white text-[12.5px] font-semibold disabled:opacity-50" data-testid="public-approval-backlog-btn">
                                <Archive size={13} /> Keep in backlog
                            </button>
                            <button onClick={() => decide('request_info')} disabled={!!decidingAction}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-blue-600 text-white text-[12.5px] font-semibold disabled:opacity-50" data-testid="public-approval-requestinfo-btn">
                                <HelpCircle size={13} /> Request more info
                            </button>
                            <button onClick={() => decide('decline')} disabled={!!decidingAction}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-rose-600 text-white text-[12.5px] font-semibold disabled:opacity-50" data-testid="public-approval-decline-btn">
                                <ThumbsDown size={13} /> Decline
                            </button>
                        </div>
                    </div>
                )}

                {ticket.status === 'awaiting_client_validation' && ticket.client_confirmed_resolved == null && (
                    <div className="bg-white rounded-xl border border-amber-300 p-6 shadow-sm" data-testid="public-ticket-validate-panel">
                        <div className="font-semibold text-[14px] text-amber-800 mb-1">Please confirm this is resolved</div>
                        <p className="text-[12.5px] text-slate-500 mb-3">We believe we've resolved this. Please check and let us know.</p>
                        <textarea value={validateComment} onChange={e => setValidateComment(e.target.value)} rows={2} placeholder="Comment (optional)"
                                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] mb-3" data-testid="public-validate-comment-input" />
                        <div className="flex gap-2">
                            <button onClick={() => validate(true)} disabled={validating}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 text-white text-[12.5px] font-semibold disabled:opacity-50" data-testid="public-validate-confirm-btn">
                                <CheckCircle2 size={13} /> Yes, this is resolved
                            </button>
                            <button onClick={() => validate(false)} disabled={validating}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-rose-600 text-white text-[12.5px] font-semibold disabled:opacity-50" data-testid="public-validate-reopen-btn">
                                <RotateCcw size={13} /> No, reopen this
                            </button>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="font-semibold text-[14px] text-slate-800 mb-3">Conversation</div>
                    <div className="space-y-3" data-testid="public-ticket-comments">
                        {(ticket.comments || []).length === 0 && (
                            <div className="text-[13px] text-slate-400">No updates yet — we'll post here as we review your request.</div>
                        )}
                        {(ticket.comments || []).map(c => {
                            const attLookup = {}; (ticket.attachments || []).forEach(a => { attLookup[a.id] = a; });
                            const cAtts = (c.attachment_ids || []).map(id => attLookup[id]).filter(Boolean);
                            return (
                                <div key={c.id} className="rounded-lg bg-slate-50 p-3" data-testid={`public-ticket-comment-${c.id}`}>
                                    <div className="flex items-center justify-between text-[11.5px] text-slate-500">
                                        <span className="font-semibold text-slate-700">{c.author_name}</span>
                                        <span>{(c.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                                    </div>
                                    <div className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap">{c.body}</div>
                                    {cAtts.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {cAtts.map(a => (
                                                <a key={a.id} href={`${API_BASE}/public/support/attachments/${ticket.id}/${a.id}`} target="_blank" rel="noopener noreferrer"
                                                   className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-600 hover:bg-slate-100"
                                                   data-testid={`public-ticket-comment-attachment-${a.id}`}>
                                                    <Paperclip size={10} />{a.name}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <PasteDropZone onFiles={fs => setReplyFiles(prev => [...prev, ...fs])} testId="public-ticket-reply-dropzone" className="mt-4">
                    <StagedFiles files={replyFiles} onRemove={i => setReplyFiles(prev => prev.filter((_, idx) => idx !== i))} testIdPrefix="public-reply-staged-file" className="mb-2" />
                    <div className="flex gap-2">
                        <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2}
                                  placeholder="Add a reply…"
                                  className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                  data-testid="public-ticket-reply-input" />
                        <button onClick={() => replyFileRef.current?.click()} type="button"
                                className="h-10 w-10 flex items-center justify-center rounded-md border border-slate-300 text-slate-500 hover:bg-slate-50 self-end"
                                data-testid="public-ticket-reply-attach-btn">
                            <Paperclip size={15} />
                        </button>
                        <input ref={replyFileRef} type="file" multiple className="hidden" onChange={e => { const fs = withinSizeLimit(e.target.files); e.target.value = ''; setReplyFiles(prev => [...prev, ...fs]); }} />
                        <button onClick={sendReply} disabled={sending || (!reply.trim() && !replyFiles.length)}
                                className="h-10 px-4 rounded-md text-white font-semibold text-[13px] self-end disabled:opacity-50"
                                style={{ background: accent }}
                                data-testid="public-ticket-reply-btn">
                            <Send size={14} />
                        </button>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1.5">Tip: paste a screenshot (Ctrl+V / Cmd+V) or drag one onto this box.</div>
                    </PasteDropZone>
                </div>

                <div className="text-center pb-4">
                    <Link to="/support/my-requests" className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-400 hover:underline" data-testid="public-ticket-view-all-requests-link">
                        <ListChecks size={13} /> See all your requests
                    </Link>
                </div>
            </main>
        </div>
    );
}
